// Dark store routing — pure helpers (no I/O, no localization import), so
// they unit-test without the store. Contract: darkstore-api-contract.md §3b.
// Card rules follow the mockup (§10 frame 08): Add DB → routed → picked → OFD.

// ALL is the "All orders" overview (D0 + NEXT); a TP's own scope is D0 or
// NEXT.
export const SCOPES = { D0: "D0", NEXT: "NEXT", ALL: "ALL" };

export const STATUS = {
  NOT_RECEIVED: "NOT_RECEIVED",
  NOT_RACKED: "NOT_RACKED",
  PACKED: "PACKED",
  ROUTED: "ROUTED",
  PICKED: "PICKED",
  OFD: "OFD",
};

// Status → Pill tone (tokens.pill) and i18n key under DsRouting/status. As
// in the mockup, a routed TP still reads PACKED: the "DB · name" line on
// the card shows the routing.
const STATUS_CHIP = {
  NOT_RECEIVED: { tone: "disabled", key: "notReceived" },
  NOT_RACKED: { tone: "toRack", key: "notRacked" },
  PACKED: { tone: "packed", key: "packed" },
  ROUTED: { tone: "packed", key: "packed" },
  PICKED: { tone: "preferred", key: "pickedByDb" },
  OFD: { tone: "racked", key: "ofd" },
};

export function getStatusChip(status) {
  return STATUS_CHIP[status] || STATUS_CHIP.PACKED;
}

// Why a card can't change now (routing `lock`, multi-trip OFF only).
export const LOCK = {
  LIVE_OFD: "LIVE_OFD", // earlier load out with this DB: no Change / Remove
  DELIVERED_TODAY: "DELIVERED_TODAY", // delivered today: next load tomorrow
  PICKED_BY_DB: "PICKED_BY_DB", // DB has the boxes: Change / Remove need a hand-off confirm
};

export const OFD_STATE = {
  READY: "READY", // DB picked: Mark OFD enabled
  WAITING: "WAITING", // routed, not picked: "Mark OFD · after DB picks"
  DONE: "DONE", // OFD ✓
  BLOCKED: "BLOCKED", // recon pending (ofd_block): server would refuse
};

/**
 * What a card shows. Next Orders are read-only. A `lock` (multi-trip OFF)
 * takes away what the server would refuse: DELIVERED_TODAY everything,
 * LIVE_OFD Change (Mark OFD still joins the DB's open trip). PICKED_BY_DB is
 * a soft lock: Change / Remove stay but `requiresHandoffConfirm` is set, so
 * the screen asks "Boxes are with X…" and sends `confirm_handoff: true`.
 * @returns {{ addDb: boolean, change: boolean, recommended: boolean, ofd: string|null, lock: string|null }}
 */
export function getCardActions(tp) {
  const none = {
    addDb: false,
    change: false,
    recommended: false,
    ofd: null,
    lock: null,
    requiresHandoffConfirm: false,
    remove: false,
  };
  if (!tp || tp.scope !== SCOPES.D0) return none;
  if (tp.status === STATUS.OFD) return { ...none, ofd: OFD_STATE.DONE };
  if (tp.lock === LOCK.DELIVERED_TODAY) {
    return { ...none, lock: LOCK.DELIVERED_TODAY };
  }
  if (!tp.db) return { ...none, addDb: true };
  const onTrip = tp.lock === LOCK.LIVE_OFD;
  const pickedByDb = tp.lock === LOCK.PICKED_BY_DB;
  return {
    addDb: false,
    change: !onTrip,
    recommended: !pickedByDb,
    ofd: tp.ofd_block
      ? OFD_STATE.BLOCKED
      : tp.status === STATUS.PICKED
        ? OFD_STATE.READY
        : OFD_STATE.WAITING,
    lock: onTrip ? LOCK.LIVE_OFD : pickedByDb ? LOCK.PICKED_BY_DB : null,
    requiresHandoffConfirm: pickedByDb,
    // A pick with no route (db.from_pick) has nothing to un-assign.
    remove: !onTrip && !tp.db.from_pick,
  };
}

/** Name of the DB holding the boxes after a hand-off, or null. */
export function getBoxesWithName(tp) {
  if (!tp?.boxes_with || tp.status === STATUS.OFD) return null;
  return tp.boxes_with.name || String(tp.boxes_with.cp_db_id ?? "") || null;
}

/**
 * A TP_PICKED_BY_DB error (HTTP 400, stale list; detected by its `error`
 * code) from assign / unassign, as a response or a thrown axios error: `{ cx_id, db_name }` to ask the hand-off confirm and
 * retry with `confirm_handoff: true`, else null (a real error).
 */
export function getHandoffConflict(result) {
  const response = result?.response || result;
  const data = response?.data || {};
  const code = data.error || data.code;
  if (code !== "TP_PICKED_BY_DB") return null;
  return { cx_id: data.cx_id ?? null, db_name: data.db_name || "" };
}

// Server error code → i18n key (DsRouting/errors/*). Unknown codes: generic.
const ERROR_KEY = {
  TP_HAS_LIVE_OFD: "tpHasLiveOfd",
  TP_DELIVERED_TODAY: "tpDeliveredToday",
  TP_PICKED_BY_DB: "tpPickedByDb",
};

export function routingErrorKey(code) {
  const key = ERROR_KEY[code];
  return key ? `DsRouting/errors/${key}` : "DsRouting/errorGeneric";
}

// List filter pills, one at a time. The server owns the rules: each TP
// carries `pending_assign` / `pending_ofd` (contract §3b `GET /tps`).
// pending_assign = racked, no DB; pending_ofd = has a DB, not yet OFD.
export const FILTERS = {
  ALL: "ALL",
  PENDING_ASSIGN: "PENDING_ASSIGN",
  PENDING_OFD: "PENDING_OFD",
};

const FILTER_FLAG = {
  [FILTERS.PENDING_ASSIGN]: "pending_assign",
  [FILTERS.PENDING_OFD]: "pending_ofd",
};

/** The cards for a locality (null = all) and one filter pill. */
export function filterRoutingTps(tps = [], { locality = null, filter } = {}) {
  const flag = FILTER_FLAG[filter];
  return tps.filter(
    (tp) => (!locality || tp.area === locality) && (!flag || tp[flag]),
  );
}

/**
 * Counts for the header: the locality's row, or the scope's `counts`.
 * Null on a server without filter counts (the screen then hides the pills
 * and reads `progress`).
 */
export function getRoutingCounts(list, locality = null) {
  if (!list?.counts) return null;
  if (!locality) return list.counts;
  const row = (list.localities || []).find((item) => item.name === locality);
  return (
    row || {
      to_route: 0,
      routed: 0,
      total: 0,
      pending_assign: 0,
      pending_ofd: 0,
    }
  );
}

// ---- By DB (OFD and un-assignment) ------------------------------------------

/** Cards a DB can be marked OFD with now (server flag `ofd_ready`). */
export const getOfdReadyTps = (tps = []) => tps.filter((tp) => tp.ofd_ready);

/** Selection keys of every ready card (for "select all"). */
export const getOfdReadyKeys = (tps = []) =>
  getOfdReadyTps(tps).map((tp) => getTpKey(tp));

/**
 * The cx_ids to send for the selected keys: ready cards only (a stale
 * selection after a reload drops out), each customer once.
 */
export function getSelectedCxIds(tps = [], selectedKeys = []) {
  const selected = new Set(selectedKeys);
  const ids = getOfdReadyTps(tps)
    .filter((tp) => selected.has(getTpKey(tp)))
    .map((tp) => tp.cx_id);
  return Array.from(new Set(ids));
}

/** Keeps only the keys that are still ready after a reload. */
export function pruneSelection(tps = [], selectedKeys = []) {
  const ready = new Set(getOfdReadyKeys(tps));
  return selectedKeys.filter((key) => ready.has(key));
}

/** Add or remove one key from a selection. */
export const toggleKey = (selectedKeys = [], key) =>
  selectedKeys.includes(key)
    ? selectedKeys.filter((item) => item !== key)
    : selectedKeys.concat(key);

/** Mark OFD result → counts and the distinct failure codes. */
export function summarizeOfdResult(result) {
  const done = result?.done || [];
  const failed = result?.failed || [];
  return {
    doneCount: done.length,
    failedCount: failed.length,
    codes: Array.from(new Set(failed.map((row) => row.code).filter(Boolean))),
  };
}

/** Cards key on customer × EDD: one customer can have two dates. */
export function getTpKey(tp) {
  return `${tp.cx_id}|${tp.date || ""}`;
}

/**
 * The search box's query, lower-cased and trimmed. A pasted card id
 * ("TP - 4471928") searches by the id alone.
 */
export function normalizeTpSearch(search = "") {
  return String(search || "")
    .trim()
    .toLowerCase()
    .replace(/^tp\s*-?\s*/, "");
}

/**
 * Client-side customer search on the routing screens: name, TP id, area,
 * locality, address and the card's DB (or who holds its boxes).
 */
export function filterTps(tps = [], search = "") {
  const query = normalizeTpSearch(search);
  if (!query) return tps;
  return tps.filter((tp) =>
    [
      tp.cx_name,
      tp.cx_id,
      tp.area,
      tp.locality,
      tp.address,
      tp.db?.name,
      tp.boxes_with?.name,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query)),
  );
}

/** "0.8 km" / null when there's no distance. */
export function formatKm(km) {
  if (km == null || !Number.isFinite(Number(km))) return null;
  return `${Number(km).toFixed(1)} km`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const addDays = (ymd, days) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
};

/**
 * The card's EDD relative to `today` (both YYYY-MM-DD): a relative key
 * ("today" | "tomorrow") or a "30 Sep" label. Past days use the date: Hindi
 * "कल" is both yesterday and tomorrow.
 */
export function eddLabel(date, today) {
  if (!date) return null;
  if (today) {
    if (date === today) return { key: "today" };
    if (date === addDays(today, 1)) return { key: "tomorrow" };
  }
  const [, month, day] = date.split("-");
  return { text: `${Number(day)} ${MONTHS[Number(month) - 1] || month}` };
}

/**
 * Local search on the "Select delivery boy" sheet: name contains the query,
 * or the digits typed match the phone's shown digits (first 2 / last 3).
 */
export function filterDbs(dbs = [], search = "") {
  const query = String(search || "")
    .trim()
    .toLowerCase();
  if (!query) return dbs;
  const digits = query.replace(/\D/g, "");
  return dbs.filter((db) => {
    if (
      String(db.name || "")
        .toLowerCase()
        .includes(query)
    )
      return true;
    const shown = String(db.phone_masked || "").replace(/\D/g, "");
    return digits.length > 0 && shown.includes(digits);
  });
}

// ---- Returned to store (re-racking) -----------------------------------------

export const RERACK_STATE = {
  ATTEMPTED: "ATTEMPTED", // attempted today: racked for tomorrow
  NOT_ATTEMPTED: "NOT_ATTEMPTED", // handed off / still out: racked for today
  EARLIER_DAY: "EARLIER_DAY", // left its rack on an earlier day
};

/** Tag key (DsRouting/returns/state/*) and tone for a returned customer. */
export function rerackStateLook(state) {
  if (state === RERACK_STATE.ATTEMPTED)
    return { key: "attempted", tone: "amber" };
  if (state === RERACK_STATE.EARLIER_DAY)
    return { key: "earlierDay", tone: "red" };
  return { key: "notAttempted", tone: "blue" };
}

/** "x of y re-racked" for a DB row: done today out of done + left. */
export function rerackProgress(db) {
  const done = Number(db?.reracked_today || 0);
  const left = Number(db?.to_rerack || 0);
  const total = done + left;
  return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
}

/** Delivery-boy filter key of a returns DB row; null DB = "none". */
export const RETURNS_ALL_DBS = "ALL";
export const returnsDbKey = (db) =>
  db?.cp_db_id == null ? "none" : String(db.cp_db_id);

/**
 * Sections for the Returned to store screen: one per delivery boy with boxes
 * left or re-racked today, most to re-rack first (server order), narrowed to
 * one DB when filtered. A customer search keeps only the matching customers
 * and drops sections left empty.
 * @param dbs GET /returns `dbs` [{ cp_db_id, name, to_rerack, reracked_today, customers }]
 */
export function buildReturnSections(
  dbs = [],
  filterKey = RETURNS_ALL_DBS,
  search = "",
) {
  const searching = Boolean(normalizeTpSearch(search));
  return dbs
    .filter(
      (db) => filterKey === RETURNS_ALL_DBS || returnsDbKey(db) === filterKey,
    )
    .filter((db) => (db.to_rerack || 0) + (db.reracked_today || 0) > 0)
    .map((db) => ({
      key: returnsDbKey(db),
      db,
      data: filterTps(db.customers || [], search),
    }))
    .filter((section) => !searching || section.data.length > 0);
}
